/**
 * KIR gene-content haplotypes.
 *
 * Gene copy values and haplotype variation notes for the KIR haplotypes
 * described in Pyo et al., BMC Genomics 2013 — "Recombinant structures expand
 * and contract inter and intragenic diversification at the KIR locus"
 * (src/files/publications/kir-haplotypes.pdf).
 *
 * A value of 1 or 2 is the number of gene copies recorded at that position;
 * null means no copy was recorded. Copy counts do not describe the physical
 * order of duplicated segments. C and T suffixes distinguish centromeric and
 * telomeric entries, and 2DS35 represents the 2DS3/2DS5 group. A question mark
 * in a note marks an interpretation the source left unresolved.
 *
 * Chromosome counts and population frequencies are NOT included here — those
 * live only in the static figure (images/techpage/kir-table-2.png). Each row
 * may carry optional `chromosomes` and `frequency` fields when that data is
 * available as data rather than as an image.
 */
export const KIR_HAPLOTYPES = [
  {
    name: 'cA01|tA01',
    local: 'A',
    note: '',
    values: {
      '3DL3': 1,
      '2DL3': 1,
      '2DS2': null,
      '2DL2': null,
      '2DL5C': null,
      '2DS35C': null,
      '2DP1': 1,
      '2DL1': 1,
      '3DP1': 1,
      '2DL4': 1,
      '3DL1': 1,
      '3DS1': null,
      '2DL5T': null,
      '2DS35T': null,
      '2DS1': null,
      '2DS4': 1,
      '3DL2': 1,
    },
  },
  {
    name: 'cA01|tA01-ins3',
    local: 'A-ins3',
    note: '3DP1-2DL4-3DL1 insertion',
    values: {
      '3DL3': 1,
      '2DL3': 1,
      '2DS2': null,
      '2DL2': null,
      '2DL5C': null,
      '2DS35C': null,
      '2DP1': 1,
      '2DL1': 1,
      '3DP1': 2,
      '2DL4': 2,
      '3DL1': 2,
      '3DS1': null,
      '2DL5T': null,
      '2DS35T': null,
      '2DS1': null,
      '2DS4': 1,
      '3DL2': 1,
    },
  },
  {
    name: 'cA01|tA01-ins4',
    local: 'A-ins4',
    note: '3DP1-2DL4-3DS1 insertion',
    values: {
      '3DL3': 1,
      '2DL3': 1,
      '2DS2': null,
      '2DL2': null,
      '2DL5C': null,
      '2DS35C': null,
      '2DP1': 1,
      '2DL1': 1,
      '3DP1': 2,
      '2DL4': 2,
      '3DL1': 1,
      '3DS1': 1,
      '2DL5T': null,
      '2DS35T': null,
      '2DS1': null,
      '2DS4': 1,
      '3DL2': 1,
    },
  },
  {
    name: 'cA01|tA01-ins5',
    local: 'A-ins5',
    note: '3DP1-2DL4-3DS1-2DL5-2DS35-2DL1hyb insertion',
    values: {
      '3DL3': 1,
      '2DL3': 1,
      '2DS2': null,
      '2DL2': null,
      '2DL5C': null,
      '2DS35C': null,
      '2DP1': 1,
      '2DL1': 1,
      '3DP1': 2,
      '2DL4': 2,
      '3DL1': 1,
      '3DS1': 1,
      '2DL5T': 1,
      '2DS35T': 1,
      '2DS1': null,
      '2DS4': 1,
      '3DL2': 1,
    },
  },
  {
    name: 'cA01|tA01-hybd1',
    local: 'A-hybd1',
    note: '3DL1/2 hybrid and 2DS4 deletion',
    values: {
      '3DL3': 1,
      '2DL3': 1,
      '2DS2': null,
      '2DL2': null,
      '2DL5C': null,
      '2DS35C': null,
      '2DP1': 1,
      '2DL1': 1,
      '3DP1': 1,
      '2DL4': 1,
      '3DL1': 1,
      '3DS1': null,
      '2DL5T': null,
      '2DS35T': null,
      '2DS1': null,
      '2DS4': null,
      '3DL2': null,
    },
  },
  {
    name: 'KIR-cA01|tB01',
    local: 'KIR-AB',
    note: '',
    values: {
      '3DL3': 1,
      '2DL3': 1,
      '2DS2': null,
      '2DL2': null,
      '2DL5C': null,
      '2DS35C': null,
      '2DP1': 1,
      '2DL1': 1,
      '3DP1': 1,
      '2DL4': 1,
      '3DL1': null,
      '3DS1': 1,
      '2DL5T': 1,
      '2DS35T': 1,
      '2DS1': 1,
      '2DS4': null,
      '3DL2': 1,
    },
  },
  {
    name: 'cA01|tB01',
    local: 'AB',
    note: '',
    values: {
      '3DL3': 1,
      '2DL3': 1,
      '2DS2': null,
      '2DL2': null,
      '2DL5C': null,
      '2DS35C': null,
      '2DP1': 1,
      '2DL1': 1,
      '3DP1': 1,
      '2DL4': 1,
      '3DL1': null,
      '3DS1': 1,
      '2DL5T': 1,
      '2DS35T': 1,
      '2DS1': 1,
      '2DS4': null,
      '3DL2': 1,
    },
  },
  {
    name: 'cA01|tB01-del6',
    local: 'AB-del6',
    note: '3DP1-2DL4-3DS1 deletion',
    values: {
      '3DL3': 1,
      '2DL3': 1,
      '2DS2': null,
      '2DL2': null,
      '2DL5C': null,
      '2DS35C': null,
      '2DP1': 1,
      '2DL1': 1,
      '3DP1': null,
      '2DL4': null,
      '3DL1': null,
      '3DS1': null,
      '2DL5T': 1,
      '2DS35T': 1,
      '2DS1': 1,
      '2DS4': null,
      '3DL2': 1,
    },
  },
  {
    name: 'cA01|tB01-del7',
    local: 'AB-del7',
    note: '2DL1 to 2DS35 deletion, 2DL1/S1 hybrid, 3DL2*007-deletion',
    values: {
      '3DL3': 1,
      '2DL3': 1,
      '2DS2': null,
      '2DL2': null,
      '2DL5C': null,
      '2DS35C': null,
      '2DP1': 1,
      '2DL1': null,
      '3DP1': null,
      '2DL4': null,
      '3DL1': null,
      '3DS1': null,
      '2DL5T': null,
      '2DS35T': null,
      '2DS1': 1,
      '2DS4': null,
      '3DL2': 1,
    },
  },
  {
    name: 'cA01|tB01-del8',
    local: 'AB-del8',
    note: '2DL3 to 2DS35 deletion, 2DL3/2DP1, 2DL1/S1 hybrid, 3DL2*007-deletion',
    values: {
      '3DL3': 1,
      '2DL3': null,
      '2DS2': null,
      '2DL2': null,
      '2DL5C': null,
      '2DS35C': null,
      '2DP1': null,
      '2DL1': null,
      '3DP1': null,
      '2DL4': null,
      '3DL1': null,
      '3DS1': null,
      '2DL5T': null,
      '2DS35T': null,
      '2DS1': 1,
      '2DS4': null,
      '3DL2': 1,
    },
  },
  {
    name: 'cA01|tB01-ins3',
    local: 'AB-ins3',
    note: '3DP1-2DL4-3DL1 insertion',
    values: {
      '3DL3': 1,
      '2DL3': 1,
      '2DS2': null,
      '2DL2': null,
      '2DL5C': null,
      '2DS35C': null,
      '2DP1': 1,
      '2DL1': 1,
      '3DP1': 2,
      '2DL4': 2,
      '3DL1': 1,
      '3DS1': 1,
      '2DL5T': 1,
      '2DS35T': 1,
      '2DS1': 1,
      '2DS4': null,
      '3DL2': 1,
    },
  },
  {
    name: 'cA01|tB01-mut-2DS1-del',
    local: 'AB-mut-2DS1-del',
    note: '2DS1 deletion?',
    values: {
      '3DL3': 1,
      '2DL3': 1,
      '2DS2': null,
      '2DL2': null,
      '2DL5C': null,
      '2DS35C': null,
      '2DP1': 1,
      '2DL1': 1,
      '3DP1': 1,
      '2DL4': 1,
      '3DL1': null,
      '3DS1': 1,
      '2DL5T': 1,
      '2DS35T': 1,
      '2DS1': null,
      '2DS4': null,
      '3DL2': 1,
    },
  },
  {
    name: 'cB01|tB01',
    local: 'B',
    note: '',
    values: {
      '3DL3': 1,
      '2DL3': null,
      '2DS2': 1,
      '2DL2': 1,
      '2DL5C': 1,
      '2DS35C': 1,
      '2DP1': 1,
      '2DL1': 1,
      '3DP1': 1,
      '2DL4': 1,
      '3DL1': null,
      '3DS1': 1,
      '2DL5T': 1,
      '2DS35T': 1,
      '2DS1': 1,
      '2DS4': null,
      '3DL2': 1,
    },
  },
  {
    name: 'cB01|tB01-del3',
    local: 'B-del3',
    note: '2DL2 deletion',
    values: {
      '3DL3': 1,
      '2DL3': null,
      '2DS2': 1,
      '2DL2': null,
      '2DL5C': 1,
      '2DS35C': 1,
      '2DP1': 1,
      '2DL1': 1,
      '3DP1': 1,
      '2DL4': 1,
      '3DL1': null,
      '3DS1': 1,
      '2DL5T': 1,
      '2DS35T': 1,
      '2DS1': 1,
      '2DS4': null,
      '3DL2': 1,
    },
  },
  {
    name: 'cB01|tB01-del7',
    local: 'B-del7',
    note: '2DL1 to 2DS35 deletion',
    values: {
      '3DL3': 1,
      '2DL3': null,
      '2DS2': 1,
      '2DL2': 1,
      '2DL5C': 1,
      '2DS35C': 1,
      '2DP1': 1,
      '2DL1': null,
      '3DP1': null,
      '2DL4': null,
      '3DL1': null,
      '3DS1': null,
      '2DL5T': null,
      '2DS35T': null,
      '2DS1': 1,
      '2DS4': null,
      '3DL2': 1,
    },
  },
  {
    name: 'cB01|tB01-del10',
    local: 'B-del10',
    note: '2DL2 to 2DS35 deletion, 2DS2-2DS35 hybrid',
    values: {
      '3DL3': 1,
      '2DL3': null,
      '2DS2': 1,
      '2DL2': null,
      '2DL5C': null,
      '2DS35C': null,
      '2DP1': 1,
      '2DL1': 1,
      '3DP1': 1,
      '2DL4': 1,
      '3DL1': null,
      '3DS1': 1,
      '2DL5T': 1,
      '2DS35T': 1,
      '2DS1': 1,
      '2DS4': null,
      '3DL2': 1,
    },
  },
  {
    name: 'cB01|tB01-ins3',
    local: 'B-ins3',
    note: '3DP1-2DL4-3DL1 insertion',
    values: {
      '3DL3': 1,
      '2DL3': null,
      '2DS2': 1,
      '2DL2': 1,
      '2DL5C': 1,
      '2DS35C': 1,
      '2DP1': 1,
      '2DL1': 1,
      '3DP1': 2,
      '2DL4': 2,
      '3DL1': 1,
      '3DS1': 1,
      '2DL5T': 1,
      '2DS35T': 1,
      '2DS1': 1,
      '2DS4': null,
      '3DL2': 1,
    },
  },
  {
    name: 'cB01|tB01-ins4',
    local: 'B-ins4',
    note: '3DP1-2DL4-3DS1 insertion',
    values: {
      '3DL3': 1,
      '2DL3': null,
      '2DS2': 1,
      '2DL2': 1,
      '2DL5C': 1,
      '2DS35C': 1,
      '2DP1': 1,
      '2DL1': 1,
      '3DP1': 2,
      '2DL4': 2,
      '3DL1': null,
      '3DS1': 2,
      '2DL5T': 1,
      '2DS35T': 1,
      '2DS1': 1,
      '2DS4': null,
      '3DL2': 1,
    },
  },
  {
    name: 'cB01|tB01-ins5',
    local: 'B-ins5',
    note: '3DP1-2DL4-3DS1-2DL5-2DS35-2DL1hyb insertion',
    values: {
      '3DL3': 1,
      '2DL3': null,
      '2DS2': 1,
      '2DL2': 1,
      '2DL5C': 1,
      '2DS35C': 1,
      '2DP1': 1,
      '2DL1': 1,
      '3DP1': 2,
      '2DL4': 2,
      '3DL1': 1,
      '3DS1': 1,
      '2DL5T': 2,
      '2DS35T': 1,
      '2DS1': 1,
      '2DS4': null,
      '3DL2': 1,
    },
  },
  {
    name: 'cB01|tB01-hybd2',
    local: 'B-hybd2',
    note: '2DS2-2DL2 hybrid?',
    values: {
      '3DL3': 1,
      '2DL3': null,
      '2DS2': null,
      '2DL2': null,
      '2DL5C': 1,
      '2DS35C': 1,
      '2DP1': 1,
      '2DL1': 1,
      '3DP1': 1,
      '2DL4': 1,
      '3DL1': null,
      '3DS1': 1,
      '2DL5T': 1,
      '2DS35T': 1,
      '2DS1': 1,
      '2DS4': null,
      '3DL2': 1,
    },
  },
  {
    name: 'cB02|tA01',
    local: 'BA1',
    note: '',
    values: {
      '3DL3': 1,
      '2DL3': null,
      '2DS2': 1,
      '2DL2': 1,
      '2DL5C': null,
      '2DS35C': null,
      '2DP1': null,
      '2DL1': null,
      '3DP1': 1,
      '2DL4': 1,
      '3DL1': 1,
      '3DS1': null,
      '2DL5T': null,
      '2DS35T': null,
      '2DS1': null,
      '2DS4': 1,
      '3DL2': 1,
    },
  },
  {
    name: 'cB02|tA01-del3',
    local: 'BA1-del3',
    note: '2DS2 deletion',
    values: {
      '3DL3': 1,
      '2DL3': null,
      '2DS2': null,
      '2DL2': 1,
      '2DL5C': null,
      '2DS35C': null,
      '2DP1': null,
      '2DL1': null,
      '3DP1': 1,
      '2DL4': 1,
      '3DL1': 1,
      '3DS1': null,
      '2DL5T': null,
      '2DS35T': null,
      '2DS1': null,
      '2DS4': 1,
      '3DL2': 1,
    },
  },
  {
    name: 'cB02|tA01-del5',
    local: 'BA1-del5',
    note: '3DP1-2DL4-3DL1 deletion',
    values: {
      '3DL3': 1,
      '2DL3': null,
      '2DS2': 1,
      '2DL2': 1,
      '2DL5C': null,
      '2DS35C': null,
      '2DP1': null,
      '2DL1': null,
      '3DP1': null,
      '2DL4': null,
      '3DL1': null,
      '3DS1': null,
      '2DL5T': null,
      '2DS35T': null,
      '2DS1': null,
      '2DS4': 1,
      '3DL2': 1,
    },
  },
  {
    name: 'cB02|tA01-ins3',
    local: 'BA1-ins3',
    note: '3DP1-2DL4-3DL1 insertion',
    values: {
      '3DL3': 1,
      '2DL3': null,
      '2DS2': 1,
      '2DL2': 1,
      '2DL5C': null,
      '2DS35C': null,
      '2DP1': null,
      '2DL1': null,
      '3DP1': 2,
      '2DL4': 2,
      '3DL1': 2,
      '3DS1': null,
      '2DL5T': null,
      '2DS35T': null,
      '2DS1': null,
      '2DS4': 1,
      '3DL2': 1,
    },
  },
  {
    name: 'cB02|tA01-ins4',
    local: 'BA1-ins4',
    note: '3DP1-2DL4-3DS1 insertion',
    values: {
      '3DL3': 1,
      '2DL3': null,
      '2DS2': 1,
      '2DL2': 1,
      '2DL5C': null,
      '2DS35C': null,
      '2DP1': null,
      '2DL1': null,
      '3DP1': 2,
      '2DL4': 2,
      '3DL1': 1,
      '3DS1': 1,
      '2DL5T': null,
      '2DS35T': null,
      '2DS1': null,
      '2DS4': 1,
      '3DL2': 1,
    },
  },
  {
    name: 'cB02|tA01-ins5',
    local: 'BA1-ins5',
    note: '3DP1-2DL4-3DS1-2DL5-2DS35 insertion',
    values: {
      '3DL3': 1,
      '2DL3': null,
      '2DS2': 1,
      '2DL2': 1,
      '2DL5C': null,
      '2DS35C': null,
      '2DP1': null,
      '2DL1': null,
      '3DP1': 2,
      '2DL4': 2,
      '3DL1': 1,
      '3DS1': 1,
      '2DL5T': 1,
      '2DS35T': 1,
      '2DS1': null,
      '2DS4': 1,
      '3DL2': 1,
    },
  },
  {
    name: 'cB01|tA01',
    local: 'BA2',
    note: '',
    values: {
      '3DL3': 1,
      '2DL3': null,
      '2DS2': 1,
      '2DL2': 1,
      '2DL5C': 1,
      '2DS35C': 1,
      '2DP1': 1,
      '2DL1': 1,
      '3DP1': 1,
      '2DL4': 1,
      '3DL1': 1,
      '3DS1': null,
      '2DL5T': null,
      '2DS35T': null,
      '2DS1': null,
      '2DS4': 1,
      '3DL2': 1,
    },
  },
  {
    name: 'cB01|tA01-del3',
    local: 'BA2-del3',
    note: '2DS2 deletion',
    values: {
      '3DL3': 1,
      '2DL3': null,
      '2DS2': null,
      '2DL2': 1,
      '2DL5C': 1,
      '2DS35C': 1,
      '2DP1': 1,
      '2DL1': 1,
      '3DP1': 1,
      '2DL4': 1,
      '3DL1': 1,
      '3DS1': null,
      '2DL5T': null,
      '2DS35T': null,
      '2DS1': null,
      '2DS4': 1,
      '3DL2': 1,
    },
  },
  {
    name: 'cB01|tA01-del5',
    local: 'BA2-del5',
    note: '3DP1-2DL4-3DL1 deletion',
    values: {
      '3DL3': 1,
      '2DL3': null,
      '2DS2': 1,
      '2DL2': 1,
      '2DL5C': 1,
      '2DS35C': 1,
      '2DP1': 1,
      '2DL1': 1,
      '3DP1': null,
      '2DL4': null,
      '3DL1': null,
      '3DS1': null,
      '2DL5T': null,
      '2DS35T': null,
      '2DS1': null,
      '2DS4': 1,
      '3DL2': 1,
    },
  },
  {
    name: 'cB01|tA01-del9',
    local: 'BA2-del9',
    note: '2DL5 to 2DP1 deletion',
    values: {
      '3DL3': 1,
      '2DL3': null,
      '2DS2': 1,
      '2DL2': 1,
      '2DL5C': null,
      '2DS35C': null,
      '2DP1': null,
      '2DL1': 1,
      '3DP1': 1,
      '2DL4': 1,
      '3DL1': 1,
      '3DS1': null,
      '2DL5T': null,
      '2DS35T': null,
      '2DS1': null,
      '2DS4': 1,
      '3DL2': 1,
    },
  },
  {
    name: 'cB01|tA01-del10',
    local: 'BA2-del10',
    note: '2DL2 to 2DS35 deletion, 2DS2-2DS35 hybrid',
    values: {
      '3DL3': 1,
      '2DL3': null,
      '2DS2': 1,
      '2DL2': null,
      '2DL5C': null,
      '2DS35C': null,
      '2DP1': 1,
      '2DL1': 1,
      '3DP1': 1,
      '2DL4': 1,
      '3DL1': 1,
      '3DS1': null,
      '2DL5T': null,
      '2DS35T': null,
      '2DS1': null,
      '2DS4': 1,
      '3DL2': 1,
    },
  },
  {
    name: 'cB01|tA01-ins3',
    local: 'BA2-ins3',
    note: '3DP1-2DL4-3DL1 insertion',
    values: {
      '3DL3': 1,
      '2DL3': null,
      '2DS2': 1,
      '2DL2': 1,
      '2DL5C': 1,
      '2DS35C': 1,
      '2DP1': 1,
      '2DL1': 1,
      '3DP1': 2,
      '2DL4': 2,
      '3DL1': 2,
      '3DS1': null,
      '2DL5T': null,
      '2DS35T': null,
      '2DS1': null,
      '2DS4': 1,
      '3DL2': 1,
    },
  },
  {
    name: 'cB01|tA01-ins4',
    local: 'BA2-ins4',
    note: '3DP1-2DL4-3DS1 insertion',
    values: {
      '3DL3': 1,
      '2DL3': null,
      '2DS2': 1,
      '2DL2': 1,
      '2DL5C': 1,
      '2DS35C': 1,
      '2DP1': 1,
      '2DL1': 1,
      '3DP1': 2,
      '2DL4': 2,
      '3DL1': 1,
      '3DS1': 1,
      '2DL5T': null,
      '2DS35T': null,
      '2DS1': null,
      '2DS4': 1,
      '3DL2': 1,
    },
  },
  {
    name: 'cB01|tA01-ins5',
    local: 'BA2-ins5',
    note: '3DP1-2DL4-3DS1-2DL5-2DS35-2DL1hyb insertion',
    values: {
      '3DL3': 1,
      '2DL3': null,
      '2DS2': 1,
      '2DL2': 1,
      '2DL5C': 1,
      '2DS35C': 1,
      '2DP1': 1,
      '2DL1': 1,
      '3DP1': 2,
      '2DL4': 2,
      '3DL1': 1,
      '3DS1': 1,
      '2DL5T': 1,
      '2DS35T': 1,
      '2DS1': null,
      '2DS4': 1,
      '3DL2': 1,
    },
  },
  {
    name: 'cB01|tA01-hybd1',
    local: 'BA2-hybd1',
    note: '3DL1/2 hybrid and 2DS4 deletion',
    values: {
      '3DL3': 1,
      '2DL3': null,
      '2DS2': 1,
      '2DL2': 1,
      '2DL5C': 1,
      '2DS35C': 1,
      '2DP1': 1,
      '2DL1': 1,
      '3DP1': 1,
      '2DL4': 1,
      '3DL1': 1,
      '3DS1': null,
      '2DL5T': null,
      '2DS35T': null,
      '2DS1': null,
      '2DS4': null,
      '3DL2': null,
    },
  },
  {
    name: 'cB01|tA01-hybd2',
    local: 'BA2-hybd2',
    note: '2DS2-2DL2 hybrid?',
    values: {
      '3DL3': 1,
      '2DL3': null,
      '2DS2': null,
      '2DL2': null,
      '2DL5C': 1,
      '2DS35C': 1,
      '2DP1': 1,
      '2DL1': 1,
      '3DP1': 1,
      '2DL4': 1,
      '3DL1': 1,
      '3DS1': null,
      '2DL5T': null,
      '2DS35T': null,
      '2DS1': null,
      '2DS4': 1,
      '3DL2': 1,
    },
  },
  {
    name: 'cB01|tA01-del3-del9',
    local: 'BA2-del3-del9',
    note: '2DS2 deletion and 2DL5 to 2DP1 deletion',
    values: {
      '3DL3': 1,
      '2DL3': null,
      '2DS2': null,
      '2DL2': 1,
      '2DL5C': null,
      '2DS35C': null,
      '2DP1': null,
      '2DL1': 1,
      '3DP1': 1,
      '2DL4': 1,
      '3DL1': 1,
      '3DS1': null,
      '2DL5T': null,
      '2DS35T': null,
      '2DS1': null,
      '2DS4': 1,
      '3DL2': 1,
    },
  },
  {
    name: 'cB01|tA01-del3-del5-del9',
    local: 'BA2-del3-del5-del9',
    note: '2DS2 deletion and 2DL5 to 2DP1 deletion, 3DP1-2DL4-3DL1 deletion',
    values: {
      '3DL3': 1,
      '2DL3': null,
      '2DS2': null,
      '2DL2': 1,
      '2DL5C': null,
      '2DS35C': null,
      '2DP1': null,
      '2DL1': 1,
      '3DP1': null,
      '2DL4': null,
      '3DL1': null,
      '3DS1': null,
      '2DL5T': null,
      '2DS35T': null,
      '2DS1': null,
      '2DS4': 1,
      '3DL2': 1,
    },
  },
  {
    name: 'cB01|tA01-del3-hybd1',
    local: 'BA2-del3-hybd1',
    note: '2DS2 deletion and 3DL1/2 hybrid and 2DS4 deletion',
    values: {
      '3DL3': 1,
      '2DL3': null,
      '2DS2': null,
      '2DL2': 1,
      '2DL5C': 1,
      '2DS35C': 1,
      '2DP1': 1,
      '2DL1': 1,
      '3DP1': 1,
      '2DL4': 1,
      '3DL1': 1,
      '3DS1': null,
      '2DL5T': null,
      '2DS35T': null,
      '2DS1': null,
      '2DS4': null,
      '3DL2': null,
    },
  },
  {
    name: 'cB01|tA01-del3-ins4',
    local: 'BA2-del3-ins4',
    note: '2DS2 deletion and 3DP1-2DL4-3DS1 insertion',
    values: {
      '3DL3': 1,
      '2DL3': null,
      '2DS2': null,
      '2DL2': 1,
      '2DL5C': 1,
      '2DS35C': 1,
      '2DP1': 1,
      '2DL1': 1,
      '3DP1': 2,
      '2DL4': 2,
      '3DL1': 1,
      '3DS1': 1,
      '2DL5T': null,
      '2DS35T': null,
      '2DS1': null,
      '2DS4': 1,
      '3DL2': 1,
    },
  },
  {
    name: 'cB01|tA01-del4-hybd1',
    local: 'BA2-del4-hybd1',
    note: '2DL2 deletion, 3DL1/2 hybrid, 2DS4 deletion',
    values: {
      '3DL3': 1,
      '2DL3': null,
      '2DS2': 1,
      '2DL2': null,
      '2DL5C': 1,
      '2DS35C': 1,
      '2DP1': 1,
      '2DL1': null,
      '3DP1': 1,
      '2DL4': 1,
      '3DL1': 1,
      '3DS1': null,
      '2DL5T': null,
      '2DS35T': null,
      '2DS1': null,
      '2DS4': null,
      '3DL2': null,
    },
  },
  {
    name: 'cB01|tA01-del9-hybd1',
    local: 'BA2-del9-hybd1',
    note: '2DL5 to 2DP1 deletion and 3DL1/2 hybrid and 2DS4 deletion',
    values: {
      '3DL3': 1,
      '2DL3': null,
      '2DS2': 1,
      '2DL2': 1,
      '2DL5C': null,
      '2DS35C': null,
      '2DP1': null,
      '2DL1': 1,
      '3DP1': 1,
      '2DL4': 1,
      '3DL1': 1,
      '3DS1': null,
      '2DL5T': null,
      '2DS35T': null,
      '2DS1': null,
      '2DS4': null,
      '3DL2': null,
    },
  },
  {
    name: 'cB03|tA01',
    local: 'BA2X',
    note: '2DS2-2DL2/2DL3 crossing-over',
    values: {
      '3DL3': 1,
      '2DL3': 1,
      '2DS2': null,
      '2DL2': null,
      '2DL5C': 1,
      '2DS35C': 1,
      '2DP1': 1,
      '2DL1': 1,
      '3DP1': 1,
      '2DL4': 1,
      '3DL1': 1,
      '3DS1': null,
      '2DL5T': null,
      '2DS35T': null,
      '2DS1': null,
      '2DS4': 1,
      '3DL2': 1,
    },
  },
  {
    name: 'cB03|tA01-ins4',
    local: 'BA2X-ins4',
    note: '2DS2-2DL2/2DL3 crossing-over, 3DP1-2DL4-3DS1 insertion',
    values: {
      '3DL3': 1,
      '2DL3': 1,
      '2DS2': null,
      '2DL2': null,
      '2DL5C': 1,
      '2DS35C': 1,
      '2DP1': 1,
      '2DL1': 1,
      '3DP1': 2,
      '2DL4': 2,
      '3DL1': 1,
      '3DS1': 1,
      '2DL5T': null,
      '2DS35T': null,
      '2DS1': null,
      '2DS4': 1,
      '3DL2': 1,
    },
  },
  {
    name: 'cB03|tA01-ins5',
    local: 'BA2X-ins5',
    note: '2DS2-2DL2/2DL3 crossing-over, 3DP1-2DL4-3DS1-2DL5-2DS35 insertion',
    values: {
      '3DL3': 1,
      '2DL3': 1,
      '2DS2': null,
      '2DL2': null,
      '2DL5C': 2,
      '2DS35C': 1,
      '2DP1': 1,
      '2DL1': 1,
      '3DP1': 2,
      '2DL4': 2,
      '3DL1': 1,
      '3DS1': 1,
      '2DL5T': null,
      '2DS35T': null,
      '2DS1': null,
      '2DS4': 1,
      '3DL2': 1,
    },
  },
  {
    name: 'cB03|tA01-hybd1',
    local: 'BA2X-hybd1',
    note: '2DS2-2DL2/2DL3 crossing-over and 3DL1/2 hybrid and 2DS4 deletion',
    values: {
      '3DL3': 1,
      '2DL3': 1,
      '2DS2': null,
      '2DL2': null,
      '2DL5C': 1,
      '2DS35C': 1,
      '2DP1': 1,
      '2DL1': 1,
      '3DP1': 1,
      '2DL4': 1,
      '3DL1': 1,
      '3DS1': null,
      '2DL5T': null,
      '2DS35T': null,
      '2DS1': null,
      '2DS4': null,
      '3DL2': null,
    },
  },
  {
    name: 'cB02|tB01',
    local: 'Bdel',
    note: '',
    values: {
      '3DL3': 1,
      '2DL3': null,
      '2DS2': 1,
      '2DL2': 1,
      '2DL5C': null,
      '2DS35C': null,
      '2DP1': null,
      '2DL1': null,
      '3DP1': 1,
      '2DL4': 1,
      '3DL1': null,
      '3DS1': 1,
      '2DL5T': 1,
      '2DS35T': 1,
      '2DS1': 1,
      '2DS4': null,
      '3DL2': 1,
    },
  },
  {
    name: 'cB02|tB01-del3',
    local: 'Bdel-del3',
    note: '2DS2 deletion',
    values: {
      '3DL3': 1,
      '2DL3': null,
      '2DS2': null,
      '2DL2': 1,
      '2DL5C': null,
      '2DS35C': null,
      '2DP1': null,
      '2DL1': null,
      '3DP1': 1,
      '2DL4': 1,
      '3DL1': null,
      '3DS1': 1,
      '2DL5T': 1,
      '2DS35T': 1,
      '2DS1': 1,
      '2DS4': null,
      '3DL2': 1,
    },
  },
  {
    name: 'cB02|tB01-del6',
    local: 'Bdel-del6',
    note: '3DP1-2DL4-3DS1 deletion',
    values: {
      '3DL3': 1,
      '2DL3': null,
      '2DS2': 1,
      '2DL2': 1,
      '2DL5C': null,
      '2DS35C': null,
      '2DP1': null,
      '2DL1': null,
      '3DP1': null,
      '2DL4': null,
      '3DL1': null,
      '3DS1': null,
      '2DL5T': 1,
      '2DS35T': 1,
      '2DS1': 1,
      '2DS4': null,
      '3DL2': 1,
    },
  },
  {
    name: 'cB02|tB01-ins4',
    local: 'Bdel-ins4',
    note: '3DP1-2DL4-3DS1 insertion',
    values: {
      '3DL3': 1,
      '2DL3': null,
      '2DS2': 1,
      '2DL2': 1,
      '2DL5C': null,
      '2DS35C': null,
      '2DP1': null,
      '2DL1': null,
      '3DP1': 2,
      '2DL4': 2,
      '3DL1': null,
      '3DS1': 2,
      '2DL5T': 1,
      '2DS35T': 1,
      '2DS1': 1,
      '2DS4': null,
      '3DL2': 1,
    },
  },
  {
    name: 'cB02|tB01-del3-del6',
    local: 'Bdel-del3-del6',
    note: '2DS2 deletion, 3DP1-2DL4-3DS1 deletion',
    values: {
      '3DL3': 1,
      '2DL3': null,
      '2DS2': null,
      '2DL2': 1,
      '2DL5C': null,
      '2DS35C': null,
      '2DP1': null,
      '2DL1': null,
      '3DP1': null,
      '2DL4': null,
      '3DL1': null,
      '3DS1': null,
      '2DL5T': 1,
      '2DS35T': 1,
      '2DS1': 1,
      '2DS4': null,
      '3DL2': 1,
    },
  },
  {
    name: 'cB02|tB01-del4-del6',
    local: 'Bdel-del4-del6',
    note: '2DL2-3DP1-2DL4-3DS1 deletion',
    values: {
      '3DL3': 1,
      '2DL3': null,
      '2DS2': 1,
      '2DL2': null,
      '2DL5C': null,
      '2DS35C': null,
      '2DP1': null,
      '2DL1': null,
      '3DP1': null,
      '2DL4': null,
      '3DL1': null,
      '3DS1': null,
      '2DL5T': 1,
      '2DS35T': 1,
      '2DS1': 1,
      '2DS4': null,
      '3DL2': 1,
    },
  },
  {
    name: 'cB02|tB01-mut',
    local: 'Bdel-mut',
    note: '?',
    values: {
      '3DL3': 1,
      '2DL3': null,
      '2DS2': 1,
      '2DL2': 1,
      '2DL5C': null,
      '2DS35C': null,
      '2DP1': 1,
      '2DL1': 1,
      '3DP1': null,
      '2DL4': null,
      '3DL1': null,
      '3DS1': null,
      '2DL5T': 1,
      '2DS35T': 1,
      '2DS1': 1,
      '2DS4': null,
      '3DL2': 1,
    },
  },
];

/**
 * The aligned column positions, centromeric to telomeric. Genes sharing a
 * position are alternatives at that position and share a column.
 */
export const KIR_POSITIONS = [
  ['3DL3'],
  ['2DS2'],
  ['2DL3', '2DL2'],
  ['2DL5C'],
  ['2DS35C'],
  ['2DP1'],
  ['2DL1'],
  ['3DP1'],
  ['2DL4'],
  ['3DL1', '3DS1'],
  ['2DL5T'],
  ['2DS4', '2DS35T'],
  ['2DS1'],
  ['3DL2'],
];

/** Which legend group each gene belongs to. Groups label diagram features, not
 *  insertion or deletion status. */
export const KIR_GENE_GROUPS = {
  '3DL3': 'framework',
  '2DS2': 'bAssociated',
  '2DL3': 'aAssociated',
  '2DL2': 'bAssociated',
  '2DL5C': 'bAssociated',
  '2DS35C': 'bAssociated',
  '2DP1': 'paired',
  '2DL1': 'paired',
  '3DP1': 'framework',
  '2DL4': 'framework',
  '3DL1': 'aAssociated',
  '3DS1': 'bAssociated',
  '2DL5T': 'bAssociated',
  '2DS4': 'aAssociated',
  '2DS35T': 'bAssociated',
  '2DS1': 'bAssociated',
  '3DL2': 'framework',
};

/** Legend copy, in display order. */
export const KIR_LEGEND = [
  { group: 'framework', label: 'Framework / shared' },
  { group: 'aAssociated', label: 'A-associated position' },
  { group: 'bAssociated', label: 'B-associated position' },
  { group: 'paired', label: '2DP1 / 2DL1' },
];

/**
 * Haplotype names look like `cB01|tB01-del3` or `KIR-cA01|tB01`. The family is
 * the `cX|tY` stem, without the variation suffixes.
 */
export const kirFamilyOf = (haplotype) =>
  haplotype.name.replace(/^KIR-/, '').split('-')[0];

/** The distinct families, in the order they first appear above. */
export const KIR_FAMILIES = [...new Set(KIR_HAPLOTYPES.map(kirFamilyOf))];
